import axiosInstance from '../../config/axios.config';

/**
 * ConnectionService - Agent Networking API Service
 * Handles all connection-related API calls for LeadFlow V2.0 Module 3: Networking
 */
const ConnectionService = {
    /**
     * Get connections for the current user.
     * @param {Object} params  - Query params (status, type, page, per_page,
     *                           search, sort, filter_cities, filter_specializations).
     * @param {Object} options - Axios options (e.g. { signal } from an
     *                           AbortController to cancel in-flight requests).
     * @returns {Promise} Paginated connections list.
     */
    getConnections: async (params = {}, options = {}) => {
        const response = await axiosInstance.get('/leadflow/connections', {
            params,
            ...options,
        });
        return response.data;
    },

    /**
     * Send a connection request
     * @param {Object} data - { receiver_profile_id, connection_message }
     * @returns {Promise} Created connection
     */
    sendRequest: async (data) => {
        const response = await axiosInstance.post('/leadflow/connections', data);
        return response.data;
    },

    /**
     * Accept a connection request
     * @param {number} connectionId - Connection ID
     * @returns {Promise} Updated connection
     */
    acceptRequest: async (connectionId) => {
        const response = await axiosInstance.post(`/leadflow/connections/${connectionId}/accept`);
        return response.data;
    },

    /**
     * Reject a connection request
     * @param {number} connectionId - Connection ID
     * @returns {Promise} Response
     */
    rejectRequest: async (connectionId) => {
        const response = await axiosInstance.post(`/leadflow/connections/${connectionId}/reject`);
        return response.data;
    },

    /**
     * Remove/cancel a connection
     * @param {number} connectionId - Connection ID
     * @returns {Promise} Response
     */
    removeConnection: async (connectionId) => {
        const response = await axiosInstance.delete(`/leadflow/connections/${connectionId}`);
        return response.data;
    },

    /**
     * Get connection suggestions
     * @param {number} limit - Number of suggestions to fetch
     * @returns {Promise} Suggested profiles
     */
    getSuggestions: async (limit = 10) => {
        const response = await axiosInstance.get('/leadflow/connections/suggestions', {
            params: { limit }
        });
        return response.data;
    },

    /**
     * Get network statistics
     * @returns {Promise} Network stats
     */
    getStatistics: async () => {
        const response = await axiosInstance.get('/leadflow/connections/statistics');
        return response.data;
    },

    /**
     * Get pending connection requests count (requests received by current user)
     * @returns {Promise} { success: boolean, data: { pending_count: number } }
     */
    getPendingCount: async () => {
        try {
            const response = await axiosInstance.get('/leadflow/connections', {
                params: { status: 'pending', type: 'received', per_page: 1 }
            });
            // The total from pagination gives us the count
            const total = response.data?.data?.total || response.data?.meta?.total || 0;
            return { success: true, data: { pending_count: total } };
        } catch (error) {
            console.error('Error fetching pending count:', error);
            return { success: false, data: { pending_count: 0 } };
        }
    },
};

export default ConnectionService;
